// src/pages/ChatBot/DocumentInspectModal.tsx
//
// Read-only inspector for one uploaded chatbot document, opened from the
// Documents tab's per-row Inspect button. Fetches /chatbot/documents/details
// when a document is selected and renders a labeled stats strip (size, role
// access, whole-document length, chunk count, average chunk size) above the
// document itself.
//
// The document is rendered by REUSING the Custom Answer workbench's pdf.js
// viewer (query-logs/corpus-pdf-viewer) verbatim — it fetches the PDF from the
// /chatbot/download path and owns the whole pdf.js lifecycle, so this modal
// never touches pdf.js directly. Non-PDF uploads show a note instead.

import { useEffect, useState } from 'react';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { useModalBackdropClose } from '../../hooks/useModalBackdropClose';
import Tag from './shared/Tag';
import { OrgRole } from './DocumentAccessModal';
import { coversAllRoles } from './document-roles';
import CorpusPdfViewer from './query-logs/corpus-pdf-viewer';
import DocumentRechunkModal, { ChunkingValues, ChunkingOverride } from './DocumentRechunkModal';
import './DocumentInspectModal.css';

// The row identity the panel hands to the modal: blobName drives the details
// fetch, fileName drives the viewer's /chatbot/download lookup.
export interface DocumentInspectTarget {
  blobName: string;
  fileName: string;
}

interface DocumentDetails {
  documentName: string;
  blobName: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadTime: string;
  roleIDs: string[];
  chunkCount: number;
  avgChunkTokens: number;
  avgChunkWords: number;
  documentTokens: number;
  documentWords: number;
  // Present + true only when the whole-document length was measured from chunk
  // texts (canonical unavailable) and so double-counts splitter overlap.
  documentLengthApprox?: boolean;
  // The org's active ingress chunking — the rechunk modal's prefill baseline.
  activeChunking?: ChunkingValues;
  // Present only when this document carries a per-document chunking override —
  // drives the "custom chunking" indicator and the rechunk modal's prefill.
  chunkingOverride?: ChunkingOverride;
}

interface DocumentInspectModalProps {
  doc: DocumentInspectTarget | null;
  // The tenant RBAC roles the panel already fetched, for id→name chip labels.
  // null while that fetch is pending/failed — chips then fall back to raw ids.
  roles: OrgRole[] | null;
  onClose: () => void;
}

type LoadState =
  | { phase: 'idle' }
  | { phase: 'loading'; blobName: string }
  | { phase: 'error'; blobName: string; message: string }
  | { phase: 'loaded'; blobName: string; details: DocumentDetails };

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;

// Human-readable size; the exact byte count rides along in the value's tooltip.
function formatBytes(bytes: number): string {
  if (bytes >= BYTES_PER_MB) return `${(bytes / BYTES_PER_MB).toFixed(1)} MB`;
  if (bytes >= BYTES_PER_KB) return `${Math.round(bytes / BYTES_PER_KB)} KB`;
  return `${bytes} B`;
}

// English ordinal suffix for the breakpoint-percentile readout ("90th", "91st").
function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

// Strategy-aware, human-readable summary of a per-document chunking override, for
// the Chunks-stat ⓘ indicator tooltip. Names the override's strategy and lists
// ONLY the knobs it actually set within that strategy's branch — e.g.
// "Custom chunking: semantic — max 256 tokens, breakpoint 90th pct, min 2 sentences".
function overrideSummary(override: ChunkingOverride): string {
  const { strategy } = override;
  if (!strategy) return 'Custom chunking';

  const parts: string[] = [];
  if (strategy === 'structure') {
    const knobs = override.structure ?? {};
    if (knobs.maxChunkTokens !== undefined) parts.push(`max ${knobs.maxChunkTokens} tokens`);
    if (knobs.minChunkTokens !== undefined) parts.push(`min ${knobs.minChunkTokens} tokens`);
    if (knobs.overlapRatio !== undefined) parts.push(`overlap ${knobs.overlapRatio}`);
  } else if (strategy === 'fixed') {
    const knobs = override.fixed ?? {};
    if (knobs.maxTokens !== undefined) parts.push(`max ${knobs.maxTokens} tokens`);
    if (knobs.overlapRatio !== undefined) parts.push(`overlap ${knobs.overlapRatio}`);
  } else {
    const knobs = override.semantic ?? {};
    if (knobs.maxTokens !== undefined) parts.push(`max ${knobs.maxTokens} tokens`);
    if (knobs.breakpointPercentile !== undefined) {
      parts.push(`breakpoint ${ordinal(knobs.breakpointPercentile)} pct`);
    }
    if (knobs.minSentencesPerChunk !== undefined) {
      const n = knobs.minSentencesPerChunk;
      parts.push(`min ${n} sentence${n === 1 ? '' : 's'}`);
    }
  }

  return parts.length > 0
    ? `Custom chunking: ${strategy} — ${parts.join(', ')}`
    : `Custom chunking: ${strategy}`;
}

export default function DocumentInspectModal({ doc, roles, onClose }: DocumentInspectModalProps) {
  const backdropHandlers = useModalBackdropClose(onClose);
  const [loadState, setLoadState] = useState<LoadState>({ phase: 'idle' });
  // Bumped after a successful rechunk to refetch the details in place.
  const [reloadNonce, setReloadNonce] = useState(0);
  const [rechunkOpen, setRechunkOpen] = useState(false);
  const [rechunkNote, setRechunkNote] = useState<string | null>(null);

  // The Inspect modal's Escape is suspended while the rechunk modal is open so
  // Escape only dismisses the topmost dialog.
  useEscapeClose(onClose, doc !== null && !rechunkOpen);

  const blobName = doc?.blobName ?? null;

  useEffect(() => {
    if (blobName === null) {
      setLoadState({ phase: 'idle' });
      return undefined;
    }
    // Stale-guard: a fast re-open (or unmount) while a request is in flight must
    // not let the late response overwrite the current document's state.
    let isCancelled = false;
    setLoadState({ phase: 'loading', blobName });
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/chatbot/documents/details?blobName=${encodeURIComponent(blobName)}`,
      { credentials: 'include' },
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
        return (await res.json()) as DocumentDetails;
      })
      .then((details) => {
        if (!isCancelled) setLoadState({ phase: 'loaded', blobName, details });
      })
      .catch((err: unknown) => {
        if (isCancelled) return;
        console.error('Failed to load document details:', err);
        setLoadState({ phase: 'error', blobName, message: 'Failed to load document details.' });
      });
    return () => {
      isCancelled = true;
    };
  }, [blobName, reloadNonce]);

  // Switching to a different document closes any open rechunk dialog + clears the note.
  useEffect(() => {
    setRechunkOpen(false);
    setRechunkNote(null);
  }, [blobName]);

  // Auto-dismiss the transient success note.
  useEffect(() => {
    if (!rechunkNote) return undefined;
    const timer = setTimeout(() => setRechunkNote(null), 4000);
    return () => clearTimeout(timer);
  }, [rechunkNote]);

  if (doc === null) return null;

  // Only trust state produced for the currently open document — a fast re-open
  // must not flash the previous document's stats.
  const isCurrent = loadState.phase !== 'idle' && loadState.blobName === doc.blobName;
  const details = isCurrent && loadState.phase === 'loaded' ? loadState.details : null;
  const errorMessage = isCurrent && loadState.phase === 'error' ? loadState.message : null;
  const isLoading = !details && !errorMessage;

  const isPdf = doc.fileName.toLowerCase().endsWith('.pdf');

  // Role chip label: names when the panel's role map is present, raw id otherwise.
  const roleName = (roleID: string) => {
    if (!roles) return roleID;
    return roles.find((r) => r.id === roleID)?.name ?? roleID;
  };

  // Access chips, mirroring DocumentsPanel's access cell: an open-tone
  // "Everyone" when unscoped (or every tenant role is selected), otherwise one
  // brass chip per scoped role.
  const renderRoleChips = (roleIDs: string[]) => {
    const scoped = roleIDs ?? [];
    const coversAll = coversAllRoles(scoped, roles);

    if (scoped.length === 0 || coversAll) {
      return (
        <Tag
          tone="open"
          title={coversAll ? 'All roles selected — open to everyone' : 'No role restriction'}
        >
          Everyone
        </Tag>
      );
    }
    return scoped.map((roleID) => (
      <Tag key={roleID} tone="brass">
        {roleName(roleID)}
      </Tag>
    ));
  };

  return (
    <>
    <div className="smrtdocs-modal-overlay" {...backdropHandlers}>
      <div
        className="smrtdocs-modal mcd-inspect-modal"
        style={{
          width: '90vw',
          height: '90dvh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div className="smrtdocs-modal-header">
          <div className="mcd-inspect-header-left">
            <h2 className="mcd-inspect-title">Inspect Document</h2>
            <span className="mcd-inspect-docname" title={doc.fileName}>
              {details?.documentName || doc.fileName}
            </span>
          </div>
          <div className="mcd-inspect-header-right">
            {rechunkNote && <span className="mcd-inspect-rechunk-note">{rechunkNote}</span>}
            <button type="button" className="mcd-inspect-close" aria-label="Close" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        <div className="smrtdocs-modal-body mcd-inspect-body">
          {isLoading && (
            <div className="mcd-inspect-loading">
              <span className="mcd-inspect-spinner" />
              Loading document details…
            </div>
          )}
          {errorMessage && <div className="mcd-inspect-error">{errorMessage}</div>}
          {details && (
            <div className="mcd-inspect-stats">
              <div className="mcd-inspect-stat">
                <span className="mcd-inspect-stat-label">Size</span>
                <span className="mcd-inspect-stat-value" title={`${details.sizeBytes} bytes`}>
                  {formatBytes(details.sizeBytes)}
                </span>
              </div>
              <div className="mcd-inspect-stat">
                <span className="mcd-inspect-stat-label">Access</span>
                <span className="mcd-inspect-stat-chips">{renderRoleChips(details.roleIDs)}</span>
              </div>
              <div className="mcd-inspect-stat">
                <span className="mcd-inspect-stat-label">Document length</span>
                <span className="mcd-inspect-stat-value">
                  ~{details.documentTokens} tokens · {details.documentWords} words
                  {details.documentLengthApprox && (
                    <span
                      className="mcd-inspect-stat-approx"
                      title="Approximate — measured from chunk text because the canonical document was unavailable"
                    >
                      {' '}
                      approx.
                    </span>
                  )}
                </span>
              </div>
              <div className="mcd-inspect-stat">
                <span className="mcd-inspect-stat-label">Chunks</span>
                <span className="mcd-inspect-stat-value">
                  {details.chunkCount}
                  {details.chunkingOverride && (
                    <span
                      className="mcd-inspect-custom-info"
                      role="img"
                      aria-label={overrideSummary(details.chunkingOverride)}
                      title={overrideSummary(details.chunkingOverride)}
                    >
                      ⓘ
                    </span>
                  )}
                </span>
              </div>
              <div className="mcd-inspect-stat">
                <span className="mcd-inspect-stat-label">Avg chunk</span>
                <span className="mcd-inspect-stat-value">
                  ~{details.avgChunkTokens} tokens · {details.avgChunkWords} words
                </span>
              </div>
              <button
                type="button"
                className="mcd-inspect-rechunk-btn"
                onClick={() => setRechunkOpen(true)}
                disabled={!details || !details.activeChunking}
                title="Re-process this document under a chosen chunking configuration"
              >
                Rechunk
              </button>
            </div>
          )}

          <div className="mcd-inspect-viewer">
            {isPdf ? (
              <CorpusPdfViewer fileName={doc.fileName} />
            ) : (
              <div className="mcd-inspect-nopreview">Preview available for PDF documents only.</div>
            )}
          </div>
        </div>
      </div>
    </div>

    {rechunkOpen && details && details.activeChunking && (
      <DocumentRechunkModal
        blobName={doc.blobName}
        activeChunking={details.activeChunking}
        chunkingOverride={details.chunkingOverride}
        onClose={() => setRechunkOpen(false)}
        onRechunked={() => {
          setReloadNonce((n) => n + 1);
          setRechunkNote('Document re-processed.');
        }}
      />
    )}
    </>
  );
}
